import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Building2, FileText, Upload, Loader2, ChevronDown, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import AskRootyButton from '../components/AskRootyButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtPct = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  return `${v >= 0 ? '' : ''}${v.toFixed(1)}%`;
};
const fmtNum = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  return v.toLocaleString();
};
const fmtVal = (row) => {
  if (row.kind === 'dollars') return fmtMoney(row.value);
  if (row.kind === 'number') return fmtNum(row.value);
  if (row.kind === 'percent') return row.value ? `${row.value.toFixed(2)}%` : '—';
  return row.value || '—';
};
const fmtDelta = (cur, ly, kind) => {
  if (!cur || !ly) return null;
  const d = cur - ly;
  if (Math.abs(d) < 0.01) return null;
  if (kind === 'percent') {
    return { delta: d, isPercentPoints: true };
  }
  const pct = ly !== 0 ? (d / ly) * 100 : 0;
  return { dollars: d, pct };
};

function PLTable({ currentRows, lyRows, title }) {
  return (
    <div className="overflow-x-auto" data-testid={`pl-table-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-3 py-2 text-left font-semibold">Line Item</th>
            <th className="px-3 py-2 text-right font-semibold">Current</th>
            <th className="px-3 py-2 text-right font-semibold">Last Year</th>
            <th className="px-3 py-2 text-right font-semibold">Variance</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((row, i) => {
            const ly = lyRows[i] || {};
            const delta = fmtDelta(row.value, ly.value, row.kind);
            const isHero = ['Net Sales', 'Customer Count', 'Average Check', 'Food Cost %', 'Labour %'].includes(row.label);
            // For percent rows, lower is BETTER for cost lines
            const costMetric = ['Food Cost %', 'Paper Cost %', 'Oil Cost %', 'Labour %', 'Food Waste %'].includes(row.label);
            const isGood = delta && (
              row.kind === 'percent' && costMetric
                ? delta.delta < 0  // cost going down is good
                : (delta.pct || delta.delta) >= 0
            );
            return (
              <tr
                key={row.key}
                className={`border-b border-slate-50 hover:bg-slate-50 ${isHero ? 'bg-orange-50/30' : ''}`}
              >
                <td className={`px-3 py-2 ${isHero ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{row.label}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${isHero ? 'font-bold text-slate-900' : 'text-slate-800'}`}>{fmtVal(row)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-400">{fmtVal(ly)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {delta ? (
                    delta.isPercentPoints ? (
                      <span className={isGood ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {delta.delta > 0 ? '+' : ''}{delta.delta.toFixed(2)}pp
                      </span>
                    ) : (
                      <span className={isGood ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {delta.pct >= 0 ? '+' : ''}{delta.pct.toFixed(1)}%
                      </span>
                    )
                  ) : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ConsolidatedPL() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState('company'); // 'company' or store_id
  const [expandedStores, setExpandedStores] = useState({});

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/consolidated-pl/reports`);
      setReports(res.data || []);
    } catch (e) { /* silent */ }
  };

  const fetchPL = async (id) => {
    setLoading(true);
    try {
      const params = id ? `?report_id=${id}` : '';
      const res = await axios.get(`${API_URL}/consolidated-pl${params}`);
      setData(res.data);
      if (res.data?.report_id) setSelectedReport(res.data.report_id);
      // Default to user's store if they have one
      if (res.data && !res.data.empty && user?.store_id) {
        const myStore = res.data.stores.find(s =>
          s.store_id === user.store_id ||
          (s.store_name || '').toLowerCase().includes((user.store_name || '').toLowerCase())
        );
        if (myStore && view === 'company' && ['manager', 'employee', 'supervisor'].includes(user.role)) {
          setView(myStore.store_id);
        }
      }
    } catch (e) {
      toast.error('Failed to load consolidated P&L');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchPL(null);
  }, []);

  const uploadPDF = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/reports/upload-executive-summary`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success(`Parsed ${res.data.stores_found} stores from ${file.name}`);
      await fetchReports();
      await fetchPL(res.data.report_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF parse failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>;
  }

  const empty = !data || data.empty;

  return (
    <div className="space-y-5" data-testid="consolidated-pl-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <FileText className="w-6 h-6" /> Consolidated P&L
            </h1>
            <p className="text-white/80 text-sm mt-1">
              {empty ? 'Upload an Executive Summary PDF to get started' : `${data.report_info?.current_period || 'Week ending ' + (data.report_info?.week_ending || '')} · ${data.store_count} stores`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {reports.length > 1 && (
              <select
                value={selectedReport}
                onChange={(e) => fetchPL(e.target.value)}
                className="bg-white/15 text-white text-sm px-3 py-2 rounded-lg border border-white/30 focus:outline-none"
                data-testid="pl-report-selector"
              >
                {reports.map(r => (
                  <option key={r.id} value={r.id} className="text-slate-900">
                    {r.week_ending || r.period || r.filename}
                  </option>
                ))}
              </select>
            )}
            {['owner', 'area_manager', 'manager'].includes(user?.role) && (
              <>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={uploadPDF}
                  className="hidden"
                  id="pl-pdf-upload"
                  data-testid="pl-pdf-upload-input"
                />
                <label htmlFor="pl-pdf-upload">
                  <Button
                    asChild
                    disabled={uploading}
                    className="bg-white text-orange-600 hover:bg-orange-50 rounded-lg font-semibold cursor-pointer"
                  >
                    <span data-testid="upload-exec-pdf-btn">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Executive Summary PDF
                    </span>
                  </Button>
                </label>
              </>
            )}
          </div>
        </div>
      </div>

      {empty ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 mb-2">No P&L data yet.</p>
          <p className="text-sm text-slate-400">Upload an Executive Summary PDF and Rooty will extract every line item — sales, COGS, labour, royalty, rent, utilities, expenses, profit.</p>
        </Card>
      ) : (
        <>
          {/* View toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setView('company')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === 'company' ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
              data-testid="view-company"
            >
              <Building2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Company-Wide
            </button>
            {data.stores.map(s => (
              <button
                key={s.store_id}
                onClick={() => setView(s.store_id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === s.store_id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                data-testid={`view-store-${s.store_id}`}
              >
                {s.store_name}
              </button>
            ))}
            <div className="ml-auto">
              <AskRootyButton
                label="Ask Rooty about this P&L"
                dataTestId="ask-rooty-pl"
                contextPrompt={`Analyze the Executive Summary P&L for ${data.report_info?.current_period || data.report_info?.week_ending}. Highlight (1) top-line story, (2) cost lines breaching A&W targets, (3) one specific action for the operations team this week. Reference the line items by name and the store names.`}
              />
            </div>
          </div>

          {/* Active view */}
          {view === 'company' ? (
            <>
              <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-slate-900 text-lg">Company-Wide Financial Summary</h2>
                  <span className="text-xs text-slate-500">Consolidated across {data.store_count} stores</span>
                </div>
                <PLTable currentRows={data.company.current_rows} lyRows={data.company.last_year_rows} title="company" />
              </Card>
              {data.company.ops_current?.some(r => r.value) && (
                <Card className="p-5">
                  <h2 className="font-bold text-slate-900 text-lg mb-4">Operational Metrics</h2>
                  <PLTable currentRows={data.company.ops_current} lyRows={data.company.ops_last_year} title="ops" />
                </Card>
              )}
            </>
          ) : (() => {
            const s = data.stores.find(x => x.store_id === view);
            if (!s) return null;
            return (
              <>
                <Card className="p-5" key={s.store_id}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-slate-900 text-lg">{s.store_name}</h2>
                    <span className="text-xs text-slate-500">Store ID: {s.store_id}</span>
                  </div>
                  <PLTable currentRows={s.current_rows} lyRows={s.last_year_rows} title={s.store_id} />
                </Card>
                {s.ops_current?.some(r => r.value) && (
                  <Card className="p-5">
                    <h2 className="font-bold text-slate-900 text-lg mb-4">Operational Metrics — {s.store_name}</h2>
                    <PLTable currentRows={s.ops_current} lyRows={s.ops_last_year} title={`ops-${s.store_id}`} />
                  </Card>
                )}
              </>
            );
          })()}

          {/* Store comparison snapshot (always visible) */}
          {view === 'company' && (
            <Card className="p-5">
              <h2 className="font-bold text-slate-900 text-lg mb-4">Store Comparison Snapshot</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b">
                      <th className="px-3 py-2 text-left">Store</th>
                      <th className="px-3 py-2 text-right">Net Sales</th>
                      <th className="px-3 py-2 text-right">Customers</th>
                      <th className="px-3 py-2 text-right">Food %</th>
                      <th className="px-3 py-2 text-right">Labour %</th>
                      <th className="px-3 py-2 text-right">vs LY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stores.map(s => {
                      const c = s.current_rows.reduce((m, r) => ({ ...m, [r.key]: r }), {});
                      const ly = s.last_year_rows.reduce((m, r) => ({ ...m, [r.key]: r }), {});
                      const lyDelta = fmtDelta(c.net_sales?.value, ly.net_sales?.value, 'dollars');
                      return (
                        <tr key={s.store_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => setView(s.store_id)}>
                          <td className="px-3 py-2 font-medium text-slate-800">{s.store_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.net_sales?.value)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtNum(c.customer_count?.value)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{c.food_cost_pct?.value ? `${c.food_cost_pct.value.toFixed(1)}%` : '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{c.labour_pct?.value ? `${c.labour_pct.value.toFixed(1)}%` : '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {lyDelta && <span className={lyDelta.pct >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {lyDelta.pct >= 0 ? '+' : ''}{lyDelta.pct.toFixed(1)}%
                            </span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
